import {
  MouseEventHandler,
  ReactNode,
  RefAttributes,
  VideoHTMLAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type { ParticipantClickEvent, TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { useEnsureTrackRef } from '@livekit/components-react'
import { RemoteTrackPublication } from 'livekit-client'
import * as useHooks from 'usehooks-ts'

/** @public */
export interface VideoTrackProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /** The track reference of the track to render. */
  trackRef?: TrackReferenceOrPlaceholder
  onTrackClick?: (evt: ParticipantClickEvent) => void
  onSubscriptionStatusChanged?: (subscribed: boolean) => void
  manageSubscription?: boolean
  facingMode?: string
  isScreenShare?: boolean
}

/**
 * The `VideoTrack` component is responsible for rendering participant video tracks like `camera` and `screen_share`.
 * This component must have access to the participant's context, or alternatively pass it a `Participant` as a property.
 *
 * @example
 * ```tsx
 * <VideoTrack trackRef={trackRef} />
 * ```
 * @see {@link @livekit/components-react#ParticipantTile | ParticipantTile}
 * @public
 */
export const VideoTrack: (props: VideoTrackProps & RefAttributes<HTMLVideoElement>) => ReactNode =
  /* @__PURE__ */ forwardRef<HTMLVideoElement, VideoTrackProps>(function VideoTrack(
    {
      onTrackClick,
      onClick,
      onSubscriptionStatusChanged,
      trackRef,
      manageSubscription,
      facingMode,
      isScreenShare,
    }: VideoTrackProps,
    ref
  ) {
    const trackReference = useEnsureTrackRef(trackRef)
    const mediaEl = useRef<HTMLVideoElement>(null)
    useImperativeHandle(ref, () => mediaEl.current || document.createElement('video'))

    const intersectionEntry = useHooks.useIntersectionObserver({ root: mediaEl.current })
    const [debouncedIntersectionEntry] = useHooks.useDebounceValue(intersectionEntry, 3000)

    // Track subscription state
    const [isSubscribed, setIsSubscribed] = useState(trackReference.publication?.isSubscribed)

    // Handle track attachment/detachment
    useEffect(() => {
      const track = trackReference.publication?.track

      if (track && mediaEl.current) {
        track.attach(mediaEl.current)
      }

      return () => {
        if (track && mediaEl.current) {
          track.detach(mediaEl.current)
        }
      }
    }, [trackReference.publication?.track])

    // Update subscription state when it changes
    useEffect(() => {
      const handleSubscriptionChanged = () => {
        setIsSubscribed(trackReference.publication?.isSubscribed)
      }

      trackReference.participant.on('trackSubscribed', handleSubscriptionChanged)
      trackReference.participant.on('trackUnsubscribed', handleSubscriptionChanged)

      return () => {
        trackReference.participant.off('trackSubscribed', handleSubscriptionChanged)
        trackReference.participant.off('trackUnsubscribed', handleSubscriptionChanged)
      }
    }, [trackReference])

    // Handle intersection observer subscription management
    useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        debouncedIntersectionEntry?.isIntersecting === false &&
        intersectionEntry?.isIntersecting === false
      ) {
        trackReference.publication.setSubscribed(false)
      }
    }, [debouncedIntersectionEntry, trackReference, manageSubscription])

    useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        intersectionEntry?.isIntersecting === true
      ) {
        trackReference.publication.setSubscribed(true)
      }
    }, [intersectionEntry, trackReference, manageSubscription])

    // Notify subscription status changes
    useEffect(() => {
      onSubscriptionStatusChanged?.(!!isSubscribed)
    }, [isSubscribed, onSubscriptionStatusChanged])

    const clickHandler: MouseEventHandler<HTMLVideoElement> = (evt) => {
      onClick?.(evt)
      onTrackClick?.({
        participant: trackReference?.participant,
        track: trackReference?.publication,
      })
    }

    const isLocalParticipant = trackReference.participant.isLocal
    const shouldMirror = isLocalParticipant && facingMode !== 'environment' && !isScreenShare

    return (
      <Box style={{ width: '100%' }} height="full" background="grey1000" position="relative">
        <video
          ref={mediaEl}
          muted
          onClick={clickHandler}
          data-lk-facing-mode={facingMode}
          style={{
            inset: '0',
            width: '100%',
            height: '100%',
            objectFit: isScreenShare ? 'contain' : 'cover',
            objectPosition: 'center',
            transform: shouldMirror ? 'scaleX(-1)' : 'none',
          }}
        />
      </Box>
    )
  })
